/**
 * echFeeds — ONE PULL PER PAGE for the five dynamic-fill feeds (2026-08-20).
 *
 * WHY THIS EXISTS. `useEchDyn` was written for the Studio, which mounts once and
 * stays for the session. Wiring the FORM's preview rail to the same hook (Jack,
 * 2026-08-20: "i'd also like this preview if possible to update dynamic info when
 * someone is filling out the form") puts it in both views — and App renders
 * exactly one of them (`view === "form"` / `view === "studio"`), so every open and
 * close of the Studio unmounts one instance and mounts the other. The hook's
 * fetches live in `useEffect`s, so each of those mounts re-issued
 * `/sfmc-division-content`, `/sfmc-social-links`, `/refdata` and `/sfmc-content`.
 *
 * THE CACHE IS IN THE SERVICE, NOT THE VIEW, because a cache held by a component
 * dies with the component — which is the problem, not the fix. Module scope is
 * this repo's page-global (the same reasoning as `ISC_CACHE` in useEchDyn and
 * `window.__iscHtml` in the monolith).
 *
 * IT CACHES THE PROMISE, NOT THE VALUE, so two mounts racing the same route in the
 * same tick share ONE request instead of one of them re-asking. And a REJECTED
 * pull is deleted on the way out: a cached failure would be permanent for the
 * session, which would turn one flaky response into a dead rail until reload.
 *
 * NOT CACHED HERE: `getTemplateHtml` (already `cache: "force-cache"`),
 * `/sfmc-blocks` (owned by `sfmcSource.ts`, which has its own once-per-page
 * promise), and FormView's own `searchCommunities` calls for the picker — those
 * are user-typed queries, not a fill feed.
 */
import {
  getCommunityPack, getDivisionContent, getIscBlock, getRefdata, getSocialLinks, searchCommunities,
  type DivisionContent, type RefdataResponse, type SocialLinkRow, getDeRow, type DeRowResponse } from "./api";
import type { CommunityPackResponse, SfmcCommunitySearchResponse } from "../types/api";
import { fetchDirect, type DirectLevel } from "./echDirect";
import type { Pack } from "./dynamicFill";
import { spellSeedNames } from "./spell";

const INFLIGHT: Record<string, Promise<unknown>> = {};

function once<T>(key: string, run: () => Promise<T>): Promise<T> {
  const hit = INFLIGHT[key] as Promise<T> | undefined;
  if (hit) return hit;
  const p = run().catch((e) => { delete INFLIGHT[key]; throw e; });
  INFLIGHT[key] = p;
  return p;
}

/** the division roster — name + phone. Division-INDEPENDENT: one key for the page. */
export const feedDivisionContent = (): Promise<{ ok: boolean; items: DivisionContent[] }> =>
  once("division-content", getDivisionContent);

/** per-division social handles. Division-independent for the same reason. */
export const feedSocialLinks = (): Promise<{ ok: boolean; divisions: SocialLinkRow[]; markets: SocialLinkRow[] }> =>
  once("social-links", getSocialLinks);

/**
 * division-scoped picklists + the record-id/address bags
 *
 * ALSO THE PLACE PLACE NAMES REACH THE SPELL CHECKER (2026-08-22). Community,
 * masterplan, market and area names — Sendero, Edenbrooke, Greenacres — are absent
 * from en_US and change whenever a division opens a community, so they cannot be a
 * static allow-list. This is the single choke point every consumer of the reference
 * data already passes through, so seeding here means no caller has to remember to.
 * `spellSeedNames` is safe before the dictionary loads and cheap to call twice.
 */
const RETRY_MS = 900;
/** one delayed retry, then the rejection stands */
const withRetry = <T,>(run: () => Promise<T>): Promise<T> =>
  run().catch(() => new Promise<T>((res, rej) => { setTimeout(() => { run().then(res, rej); }, RETRY_MS); }));

/* ── REFDATA GETS THE SAME RETRY (2026-09-14) ─────────────────────────────────────
   Root cause behind Jie's morning (CRS, 10:14 AM): /refdata is an Airtable crawl the
   backend caches for 10 minutes, and Airtable was answering 429 (the Free workspace's
   monthly API cap) until the base moved at ~10:10. A failed /refdata leaves every pick
   without a code, so a MASTERPLAN pick (Cypress Preserve, Brunswick Plantation — both are)
   cannot roll up to its collections and nothing fills: hero on "Community Name", one
   default card. Communities still resolve by name search, which is why her three-pick set
   with Brookshire and Egret Landing filled. One retry will not outlast an outage, but it
   does cover the rebuild-in-flight 502 that a cold cache produces right after one. */
export const feedRefdata = (div: string): Promise<RefdataResponse> =>
  once("refdata:" + String(div || "").toUpperCase(), () =>
    withRetry(() => getRefdata(div)).then((r) => {
      try {
        spellSeedNames([
          ...(r.communities || []),
          ...(r.masterplans || []),
          ...(r.markets || []),
          ...(r.areas || []),
        ]);
      } catch {
        /* the checker is an extra — never let it break the reference-data load */
      }
      return r;
    }),
  );

/* ── A PACK THAT FAILS ONCE IS ASKED AGAIN, ONCE (2026-09-14) ─────────────────────
   Jie (CRS), Teams: "when I select a community, sometimes it populates the Explore Homes
   images, sometimes it doesn't" — Cypress Preserve + Brunswick Plantation gave one
   placeholder card; the same picks a minute later, and a three-pick set, filled fine.
   Nothing in the picks changed between the two, so the difference is the fetch: a
   /sfmc-content answer that 502s (an SFMC token hiccup, a gateway blip) rejects, the
   resolver records `C: null` for that pick, and the card sits on its template default
   until the pick itself changes or the page reloads. The `once` cache is not the culprit
   — a rejection is deleted from it — but nothing ever asked again. So one failed pack
   fetch is retried after a short pause before it is allowed to become "no pack". Two
   failures in a row are a real outage, and null is the honest answer then. */
/** one community's content pack, keyed by the SFMC community CODE */
export const feedCommunityPack = (code: string): Promise<CommunityPackResponse> =>
  once("pack:" + String(code || ""), () => withRetry(() => getCommunityPack(code)));

/** name → code resolution for a pick whose code /refdata didn't carry */
export const feedCommunitySearch = (q: string): Promise<SfmcCommunitySearchResponse> =>
  once("search:" + String(q || "").toLowerCase(), () => searchCommunities(q));

/** the division's own assembled Contact Us markup */
export const feedIscBlock = (div: string): Promise<{ ok: boolean; html?: string; imgCount?: number }> =>
  once("isc:" + String(div || "").toUpperCase(), () => getIscBlock(div));

/** one DE row for one division — the read half of a `dataSource` block (2026-09-02) */
export const feedDeRow = (key: string, pk: string, pkcol?: string): Promise<DeRowResponse> =>
  once("de:" + key + ":" + String(pkcol || "").toLowerCase() + ":" + String(pk || "").toUpperCase(), () => getDeRow(key, pk, pkcol));

/**
 * The DIVISION's whole inventory for one level, cached like every other feed
 * (2026-08-22).
 *
 * `fetchDirect` was called straight from the wizard, so the Homesites step re-fetched
 * `/homesites.json` every time it opened — and the community step now needs the same
 * payload to show a real home COUNT per community (Jack: "show the actual number of
 * homes"). Two callers, one route, one promise. The feed is the heaviest the worker
 * serves (measured 2.4s–60s cold), which is exactly why it should be fetched once.
 */
export const feedDirect = (level: DirectLevel, div: string): Promise<Pack[]> =>
  once("direct:" + level + ":" + String(div || "").toUpperCase(), () => fetchDirect(level, div));

/** tests only — and the one place a genuine refresh would hook in */
export function __resetEchFeeds(): void {
  for (const k of Object.keys(INFLIGHT)) delete INFLIGHT[k];
}
